/**
 * Shared gradient styling for Stripe payment components.
 * Matches Defera Zero checkout for consistent UX across Reserve, Defera Zero, etc.
 *
 * Saved cards are platinum: a quiet warm metal that sits between checkout's
 * gold Defera Miles card (milesCardStyles.js) and the green Confirm Booking
 * button, and reads the same on every payment screen.
 */
export const paymentCardGradient = {
  // Outer container for the payment method section
  container:
    'relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-transparent px-3 py-5 sm:px-5 shadow-[0_0_24px_-8px_rgba(0,0,0,0.3)] min-w-0',

  // Card button - selected
  cardSelected:
    'border-[#e7e5e4]/35 bg-gradient-to-r from-white/[0.09] via-white/[0.04] to-transparent shadow-[0_0_22px_-10px_rgba(231,229,228,0.3)]',

  // Card button - unselected
  cardUnselected:
    'border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.06]',

  // Card icon container - selected: a platinum tile.
  iconSelected:
    'bg-gradient-to-br from-[#fafaf9] via-[#d6d3d1] to-[#a8a29e] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_4px_14px_-6px_rgba(231,229,228,0.45)]',

  // Card icon container - unselected
  iconUnselected: 'bg-white/10 group-hover:bg-white/15',

  // CreditCard icon - selected
  iconColorSelected: 'text-[#1c1917]',

  // CreditCard icon - unselected
  iconColorUnselected: 'text-white/50',

  // Add new card button
  addNewCard:
    'w-full flex items-center gap-4 p-4 rounded-xl border border-dashed border-white/20 bg-white/[0.02] hover:border-white/35 hover:bg-white/[0.05] text-white/70 hover:text-white/90 text-sm font-normal transition-all duration-200',

  // Add new card icon container
  addNewCardIcon:
    'flex-shrink-0 w-10 h-10 rounded-lg border border-dashed border-white/20 flex items-center justify-center',

  // CardElement wrapper (Stripe input)
  cardElementWrapper:
    'px-3 py-5 sm:px-5 rounded-xl bg-black/40 border border-white/10 focus-within:border-white/30 focus-within:shadow-[0_0_20px_-8px_rgba(231,229,228,0.18)] transition-all duration-200 min-w-0 overflow-hidden w-full [&>div]:min-w-0 [&>div]:max-w-full',

  // Card row base (flex layout for card buttons)
  cardRowBase: 'group w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200',

  // Check badge on the selected card, and its check
  checkBadge: 'bg-gradient-to-br from-[#fafaf9] to-[#a8a29e] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]',
  checkIcon: 'text-[#1c1917]',

  // "← Use saved card instead"
  useSavedCardLink: 'text-sm text-white/70 hover:text-white font-medium transition-colors',
};

/** The same platinum for SavedPaymentMethodCard's richer card row. */
export const savedCardPlatinum = {
  rowAccent:
    'border-[#e7e5e4]/35 bg-gradient-to-br from-white/[0.09] via-white/[0.035] to-transparent shadow-[0_0_28px_-12px_rgba(231,229,228,0.35)]',
  iconAccent:
    'bg-gradient-to-br from-[#fafaf9] via-[#d6d3d1] to-[#a8a29e] border-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_4px_14px_-6px_rgba(231,229,228,0.45)]',
  iconColorAccent: 'text-[#1c1917]',
  badge: 'text-[#f5f5f4]/90 bg-white/[0.08] border border-white/20',
  check: 'bg-gradient-to-br from-[#fafaf9] to-[#a8a29e] border border-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]',
  checkIcon: 'text-[#1c1917]',
};
